import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import useMatches from '../hooks/useMatches.js';
import { getTeamProfile } from '../services/public-api.js';
import MatchSchedule from '../components/matches/MatchSchedule.jsx';
import TeamMembers from '../components/teams/TeamMembers.jsx';
import Loading from '../components/common/Loading.jsx';
import ErrorMessage from '../components/common/ErrorMessage.jsx';
import Button from '../components/common/Button.jsx';
import './CaptainDashboard.css';

export default function CaptainDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('matches'); // 'matches' or 'members'
  const { pendingMatches, scheduledMatches, loading, error, refreshMatches } =
    useMatches(selectedTeam?.teamId);

  useEffect(() => {
    if (!user?.teamId) return;
    let mounted = true;
    // Set team immediately so useMatches can start fetching
    setSelectedTeam({ teamId: user.teamId, name: user.teamName || 'My Team' });
    // Fetch actual team name from API
    getTeamProfile(user.teamId)
      .then(data => {
        if (mounted && data?.team?.name) {
          setSelectedTeam(prev => prev ? { ...prev, name: data.team.name } : prev);
        }
      })
      .catch(err => console.error('Failed to fetch team profile:', err));
    return () => { mounted = false; };
  }, [user]);

  const handleLogMatch = (match) => {
    navigate(`/captain/log-match/${match.matchId}`, { state: { match } });
  };

  const handleReviewPending = (match) => {
    navigate(`/captain/review-match/${match.matchId}`, { state: { match } });
  };

  if (!user) {
    return (
      <div className="captain-dashboard">
        <div className="auth-required">
          <h2>Authentication Required</h2>
          <p>Please log in to access the captain dashboard.</p>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </div>
      </div>
    );
  }

  if (!user.teamId) {
    return (
      <div className="captain-dashboard">
        <div className="auth-required">
          <h2>No Team Assigned</h2>
          <p>You are not currently assigned to a team. Please contact your competition organiser.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="captain-dashboard">
      <div className="dashboard-header">
        <h1>Captain Dashboard</h1>
        {selectedTeam && <p className="team-name">{selectedTeam.name}</p>}
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          Matches
        </button>
        <button
          className={`tab-btn ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Team Members
        </button>
      </div>

      {/* Matches Tab */}
      {activeTab === 'matches' && (
        <>
          {error && <ErrorMessage message={error} onRetry={refreshMatches} />}

          {loading && <Loading />}

          {!loading && !error && (
            <>
              {/* Pending Matches Section */}
              {pendingMatches.length > 0 && (
                <section className="pending-section">
                  <h2>Pending Approval</h2>
                  <div className="pending-matches">
                    {pendingMatches.map((match) => (
                      <div key={match.matchId} className="pending-card">
                        <div className="pending-header">
                          <h3>{match.team1Name} vs {match.team2Name}</h3>
                          <span className={`status-badge ${match.status}`}>
                            {match.status === 'conflict' ? 'Conflict' : 'Pending'}
                          </span>
                        </div>
                        <div className="pending-details">
                          <p>Results submitted and awaiting your approval</p>
                          {match.status === 'conflict' && (
                            <p className="conflict-notice">
                              ⚠️ Conflicting results submitted - review required
                            </p>
                          )}
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => handleReviewPending(match)}
                        >
                          Review Results
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Scheduled Matches Section */}
              <section className="scheduled-section">
                <MatchSchedule
                  matches={scheduledMatches}
                  onLogMatch={handleLogMatch}
                />
              </section>

              {/* Empty State */}
              {pendingMatches.length === 0 && scheduledMatches.length === 0 && (
                <div className="empty-state">
                  <h3>No Matches</h3>
                  <p>You have no upcoming or pending matches at this time.</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Team Members Tab */}
      {activeTab === 'members' && selectedTeam && (
        <TeamMembers teamId={selectedTeam.teamId} />
      )}
    </div>
  );
}
